import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { pwg5m4Api, ReverseCandidate } from '../../services/pwg5m4Api';
import {
  BatchAcceptanceBridge,
  pwg5m4f1AcceptanceManifest,
} from '../../features/acceptance';
import './PWG5M4Form.css';

// ---- Item state with selection ----
interface ItemState {
  item: ReverseCandidate;
  selected: boolean;
}

// ---- Component ----
// PWG5M4F1 (銷貨折讓單核准沖銷) — batch reversal queue, mirrors PWG5M5Form/
// PWG504Form's query→select→confirm→execute shape (CHAIN-17 前端棒,
// 2026-07-23).
const PWG5M4Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [queried, setQueried] = useState(false);
  const [acceptanceLastApiResponse, setAcceptanceLastApiResponse] = useState<unknown>(null);

  // ---- Derived state ----
  const selectedCount = items.filter((s) => s.selected).length;
  const allSelected = items.length > 0 && items.every((s) => s.selected);
  const someSelected = items.some((s) => s.selected) && !allSelected;
  const selectedNos = items.filter((s) => s.selected).map((s) => s.item.fg140_01);
  const candidateRecords = items.map((s) => s.item);
  const queryAcceptanceExact = useCallback(
    (key: string) => pwg5m4Api.queryCandidates(key, key),
    []
  );
  const handleAcceptanceAutoFill = useCallback(() => {
    const sample = items[0]?.item;
    if (!sample) {
      message.warning('請先查詢可沖銷折讓單；自動填表不會直接沖銷。');
      return;
    }
    setStartNo(sample.fg140_01);
    setEndNo(sample.fg140_01);
    setItems((current) => current.map((row, index) => ({ ...row, selected: index === 0 })));
    message.success('已帶入並只勾選第一張，仍需由你確認沖銷。');
  }, [items]);

  // ---- Query ----
  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await pwg5m4Api.queryCandidates(startNo, endNo);
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
      }));
      setItems(mapped);
      setAcceptanceLastApiResponse(res);
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無符合條件的銷貨折讓單');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // ---- Toggle selection ----
  const handleToggle = useCallback((no: string, checked: boolean) => {
    setItems((prev) =>
      prev.map((s) =>
        s.item.fg140_01 === no ? { ...s, selected: checked } : s
      )
    );
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    setItems((prev) => prev.map((s) => ({ ...s, selected: checked })));
  }, []);

  // ---- Execute reversal ----
  const onExecute = useCallback(() => {
    if (selectedCount === 0) {
      message.warning('請先勾選要沖銷的銷貨折讓單');
      return;
    }
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷已勾選的 ${selectedCount} 筆銷貨折讓單？此操作不可復原。`,
      okText: '確定沖銷',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        const selectedNos = items
          .filter((s) => s.selected)
          .map((s) => s.item.fg140_01);
        setExecuting(true);
        try {
          // confirmed:true -- this call only runs after the user clicked
          // "確定沖銷" in the Modal.confirm above (PWG5M4-VAL-REVERSE-
          // CONFIRM-DIALOG, human_approved 2026-07-23).
          const res = await pwg5m4Api.reverse(selectedNos, true);
          setAcceptanceLastApiResponse(res);
          const results = res.results || [];
          const failed = results.filter((r) => !r.success);
          if (failed.length === 0) {
            message.success(`沖銷完成，成功 ${results.length} 筆`);
          } else {
            message.warning(
              `成功 ${results.length - failed.length} 筆，失敗 ${failed.length} 筆`
            );
            failed.forEach((r) => message.error(`${r.doc_key}: ${r.message || '沖銷失敗'}`));
          }
          setItems([]);
          setQueried(false);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } } };
          message.error(err.response?.data?.error || '沖銷執行失敗');
        } finally {
          setExecuting(false);
        }
      },
    });
  }, [selectedCount, items]);

  // ---- Table columns ----
  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) => handleToggle(record.item.fg140_01, e.target.checked)}
        />
      ),
    },
    {
      title: '折讓單號',
      dataIndex: ['item', 'fg140_01'],
      key: 'fg140_01',
      width: 130,
      render: (v: string) => <span data-field-id="fg140_01">{v}</span>,
    },
    {
      title: '折讓日期',
      dataIndex: ['item', 'fg140_11'],
      key: 'fg140_11',
      width: 110,
      render: (v: string) => <span data-field-id="fg140_11">{v}</span>,
    },
    {
      title: '公司代號',
      dataIndex: ['item', 'fg140_12'],
      key: 'fg140_12',
      width: 100,
      render: (v: string) => <span data-field-id="fg140_12">{v}</span>,
    },
    {
      title: '折讓總淨額',
      dataIndex: ['item', 'fg140_14'],
      key: 'fg140_14',
      width: 120,
      align: 'right',
      render: (v: string) => <span data-field-id="fg140_14">{v}</span>,
    },
    {
      title: '折讓總稅額',
      dataIndex: ['item', 'fg140_15'],
      key: 'fg140_15',
      width: 120,
      align: 'right',
      render: (v: string) => <span data-field-id="fg140_15">{v}</span>,
    },
    {
      title: '核准狀態',
      dataIndex: ['item', 'fg140_80'],
      key: 'fg140_80',
      width: 90,
      render: (v: string) => <span data-field-id="fg140_80">{v}</span>,
    },
  ];

  // ---- Render ----
  return (
    <div className="pwg5m4-form">
      {(loading || executing) && (
        <div className="loading-overlay">
          {executing ? '沖銷執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                應收帳款管理
              </a>
              <span>/</span>
              <span className="current">銷貨折讓單沖銷</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>銷貨折讓單沖銷</h1>
            <span className="badge">G5M4</span>
          </div>
        </div>

        <BatchAcceptanceBridge
          manifest={pwg5m4f1AcceptanceManifest}
          records={candidateRecords}
          selectedKeys={selectedNos}
          keyOf={(record) => record.fg140_01}
          queryExact={queryAcceptanceExact}
          lastApiResponse={acceptanceLastApiResponse}
          loading={loading || executing}
          onAutoFill={handleAcceptanceAutoFill}
          autoFillDisabled={items.length === 0 || executing}
          autoFillReason={items.length === 0 ? '請先查詢候選資料。' : '只選第一張，不會自動沖銷。'}
          screenDraft={{ query: { start_no: startNo, end_no: endNo }, selected_items: selectedNos, candidates: candidateRecords }}
        />

        {/* Query Bar */}
        <div className="query-bar">
          <label>折讓單號 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 160 }}
            allowClear
            data-field-id="query-start-no"
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 160 }}
            allowClear
            data-field-id="query-end-no"
          />
          <Button
            type="primary"
            data-acceptance-action="PWG5M4-ACT-QUERY-CANDIDATES"
            onClick={onQuery}
            loading={loading}
            data-field-id="btn-query"
          >
            確定範圍
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fg140_01}
              pagination={false}
              size="small"
              scroll={{ y: 420 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入折讓單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button
            type="primary"
            data-acceptance-action="PWG5M4-ACT-REVERSE"
            size="large"
            disabled={selectedCount === 0 || executing}
            loading={executing}
            onClick={onExecute}
            data-field-id="btn-reverse"
          >
            存檔 ({selectedCount})
          </Button>
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
            data-field-id="btn-exit"
          >
            離開
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PWG5M4Form;
